import React from "react";
import { Box, CircularProgress, Typography, Fade } from "@mui/material";
import { mobiliTheme } from "../themes/mobiliTheme";

export interface LoadingSpinnerProps {
  size?: number;
  message?: string;
  color?: 'primary' | 'secondary' | 'inherit';
  thickness?: number;
  variant?: 'indeterminate' | 'determinate';
  value?: number;
  showMessage?: boolean;
  fadeIn?: boolean;
  centered?: boolean;
  overlay?: boolean;
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 40,
  message = "Lädt...",
  color = 'primary',
  thickness = 4,
  variant = 'indeterminate',
  value,
  showMessage = true,
  fadeIn = true,
  centered = true,
  overlay = false
}) => {
  
  const spinnerContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        ...(centered && {
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }),
        ...(overlay && {
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(2px)',
          borderRadius: 2,
          p: 3,
          boxShadow: mobiliTheme.shadows?.[3] || '0px 3px 5px rgba(0,0,0,0.2)',
        }),
        zIndex: overlay ? 1000 : 1,
      }}
    >
      {/* Progress Circle */}
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <CircularProgress
          size={size}
          thickness={thickness}
          color={color}
          variant={variant}
          value={value}
          sx={{
            color: color === 'primary' 
              ? mobiliTheme.palette?.primary?.main || '#1976d2'
              : color === 'secondary'
              ? mobiliTheme.palette?.secondary?.main || '#dc004e'
              : 'inherit'
          }}
        />
        
        {/* Percentage Display for Determinate */}
        {variant === 'determinate' && value !== undefined && (
          <Box
            sx={{
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="caption"
              component="div"
              color="text.secondary"
              sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}
            >
              {`${Math.round(value)}%`}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Loading Message */}
      {showMessage && message && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            textAlign: 'center',
            fontSize: '0.875rem',
            fontWeight: 500,
            opacity: 0.8,
            maxWidth: 200,
          }}
        >
          {message}
        </Typography>
      )}
    </Box>
  );

  if (fadeIn) {
    return (
      <Fade in timeout={300}>
        {spinnerContent}
      </Fade>
    );
  }

  return spinnerContent;
};

export default LoadingSpinner;
